import React, { useState, useEffect } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  Search,
  Plus,
  Trash2,
  Eye,
  Calendar,
  Clock,
  Users,
  MapPin,
  ChefHat,
  Star,
  AlertCircle,
  X,
  Mail,
  Phone,
  Edit2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getCateringServices,
  deleteCateringService,
  getCateringBookings,
  updateCateringBookingStatus,
  getCateringStats,
  formatCurrency,
  handleRestaurantApiError
} from '../../../services/restaurantApi';
import { toastSuccess, toastError } from '../../../utils/toast';
import { getCateringFormulas } from '../../../services/cateringApi';
import {
  getCateringServiceTypeLabel,
  getCateringOccasionLabel,
  getActiveDietaryLabels,
  getOfferStartingPrice,
  CATERING_PAID_OPTIONS,
} from '../../../constants/catering';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const SERVICE_BADGE = {
  active: { bg: '#DDEECB', fg: '#054625', label: 'Active' },
  inactive: { bg: '#FEF0EA', fg: '#A32C0F', label: 'Inactive' },
};

const BOOKING_BADGE = {
  pending: { bg: '#F4F1E2', fg: '#393A30', label: 'En attente' },
  confirmed: { bg: '#DDEECB', fg: '#054625', label: 'Confirmée' },
  completed: { bg: 'transparent', fg: '#051004', border: '1px solid rgba(5,16,4,0.2)', label: 'Terminée' },
  cancelled: { bg: '#FEF0EA', fg: '#A32C0F', label: 'Annulée' },
};

const OPTION_TAGS = CATERING_PAID_OPTIONS.map((o) => ({ key: o.flag, label: o.label }));

// Options choisies par le client, telles qu'enregistrées (selected_options)
const chosenOptionLabels = (selected) => {
  if (!selected) return [];
  return CATERING_PAID_OPTIONS.filter((o) => (o.id === 'staff' ? selected.staff_hours > 0 : selected[o.id]))
    .map((o) => (o.id === 'staff' ? `${o.label} (${selected.staff_hours} h)` : o.label));
};

const formatOfferPrice = (service) => {
  const { amount, perPerson } = getOfferStartingPrice(service);
  return perPerson ? `dès ${amount.toFixed(2)} €/pers.` : `forfait ${amount.toFixed(2)} €`;
};

const RestaurantCatering = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('services'); // 'services' ou 'bookings'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [cateringServices, setCateringServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [formulaNames, setFormulaNames] = useState({});

  // Le filtre dépend de l'onglet : une offre est active/inactive, une réservation a un cycle de vie.
  const statusOptions = activeTab === 'services'
    ? [
      { id: 'all', name: 'Toutes les offres' },
      { id: 'available', name: 'Actives' },
      { id: 'unavailable', name: 'Inactives' },
    ]
    : [
      { id: 'all', name: 'Tous les statuts' },
      { id: 'pending', name: 'En attente' },
      { id: 'confirmed', name: 'Confirmée' },
      { id: 'completed', name: 'Terminée' },
      { id: 'cancelled', name: 'Annulée' }
    ];

  useEffect(() => {
    loadData();
    getCateringFormulas()
      .then((list) => setFormulaNames(Object.fromEntries((list || []).map((fm) => [fm.id, fm.name]))))
      .catch(() => setFormulaNames({}));
  }, []);

  useEffect(() => {
    setFilterStatus('all');
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Charger les services, réservations et statistiques en parallèle
      const [servicesData, bookingsData, statsData] = await Promise.all([
        getCateringServices(),
        getCateringBookings('all'),
        getCateringStats()
      ]);

      // Le backend retourne directement un array pour les services
      const servicesList = Array.isArray(servicesData) ? servicesData : (servicesData.services || []);
      setCateringServices(servicesList);

      // Les réservations
      const bookingsList = Array.isArray(bookingsData) ? bookingsData : (bookingsData.bookings || []);
      setBookings(bookingsList);
      setFilteredBookings(bookingsList);

      // Les statistiques
      setStats(statsData);

      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError(handleRestaurantApiError(error));
      setLoading(false);
    }
  };

  // Fonction pour supprimer un service de traiteur
  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Supprimer définitivement cette offre ?')) {
      return;
    }
    try {
      await deleteCateringService(serviceId);
      setCateringServices(cateringServices.filter(service => service.id !== serviceId));
      await loadData();
      toastSuccess('Offre supprimée.');
    } catch (error) {
      console.error('Erreur lors de la suppression du service:', error);
      toastError(handleRestaurantApiError(error));
      setError(handleRestaurantApiError(error));
    }
  };

  // Fonction pour mettre à jour le statut d'une réservation (renvoie true si l'API l'a accepté)
  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    if (newStatus === 'cancelled' && !window.confirm('Annuler cette réservation ? Le client sera prévenu immédiatement.')) {
      return false;
    }
    try {
      await updateCateringBookingStatus(bookingId, newStatus);
      setBookings(bookings.map(booking =>
        booking.id === bookingId ? { ...booking, status: newStatus } : booking
      ));
      await loadData();
      toastSuccess('Statut de la réservation mis à jour.');
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      toastError(handleRestaurantApiError(error));
      setError(handleRestaurantApiError(error));
      return false;
    }
  };

  // Une prestation ne peut être marquée terminée qu'à partir du jour de l'événement (règle de l'API)
  const eventHasStarted = (booking) => new Date(`${booking.event_date}T00:00:00`) <= new Date();

  // Fonction pour filtrer les services
  const filterServices = () => {
    let filtered = cateringServices;

    // Filtrer par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(service =>
        service.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.service_description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrer par statut (pour les services, on filtre par is_active)
    if (filterStatus !== 'all') {
      if (filterStatus === 'available') {
        filtered = filtered.filter(service => service.is_active === true);
      } else if (filterStatus === 'unavailable') {
        filtered = filtered.filter(service => service.is_active === false);
      }
    }

    setFilteredServices(filtered);
  };

  // Appliquer les filtres quand les données ou les filtres changent
  useEffect(() => {
    filterServices();
  }, [cateringServices, searchTerm, filterStatus]);

  // Filtrer les réservations
  useEffect(() => {
    let filtered = bookings;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(booking => booking.status === filterStatus);
    }

    if (searchTerm) {
      filtered = filtered.filter(booking =>
        booking.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.contact_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.delivery_address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBookings(filtered);
  }, [bookings, searchTerm, filterStatus]);

  const getEventTypeName = getCateringServiceTypeLabel;

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'corporate':
        return <Users size={14} style={{ color: PRIMARY }} />;
      case 'wedding':
        return <Star size={14} style={{ color: TERRA }} />;
      case 'birthday':
        return <ChefHat size={14} style={{ color: '#FFC717' }} />;
      case 'conference':
        return <Calendar size={14} style={{ color: PRIMARY }} />;
      default:
        return <ChefHat size={14} style={{ color: 'rgba(5,16,4,0.5)' }} />;
    }
  };

  const bookingBadge = (status) => BOOKING_BADGE[status] || { bg: '#F4F1E2', fg: '#393A30', label: status };

  if (loading) return <PageSkeleton />;

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
            {cateringServices.length} offre{cateringServices.length > 1 ? 's' : ''} · {bookings.length} réservation{bookings.length > 1 ? 's' : ''}
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 38px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Service traiteur</h1>
        </div>
        <button
          onClick={() => navigate('/management/catering/offers/new')}
          className="inline-flex items-center gap-2"
          style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 20px', borderRadius: '999px', whiteSpace: 'nowrap' }}
        >
          <Plus size={16} />
          Nouvelle offre
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <AlertCircle size={18} />
          {error?.message || error?.toString() || 'Une erreur est survenue'}
        </div>
      )}

      {/* Onglets */}
      <div className="flex flex-wrap gap-1" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '4px', width: 'fit-content' }}>
        {[
          { key: 'services', label: 'Offres', count: cateringServices.length },
          { key: 'bookings', label: 'Réservations', count: bookings.length },
        ].map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2"
              style={{
                border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: '13.5px', fontWeight: 600,
                padding: '9px 16px', borderRadius: '999px', whiteSpace: 'nowrap',
                background: active ? PRIMARY : 'transparent',
                color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
              }}
            >
              {tab.label}
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 700, color: active ? '#FFC717' : 'rgba(5,16,4,0.4)' }}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 'clamp(12px, 1.6vw, 18px)' }}>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Offres</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em' }}>{stats?.total_services || cateringServices.length}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Actives</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em', color: PRIMARY }}>{cateringServices.filter(s => s.is_active === true).length}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Réservations</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em' }}>{stats?.total_bookings || bookings.length}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Revenus (terminées)</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em', color: TERRA }}>{formatCurrency(stats?.total_revenue || 0)}</strong>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label className="flex items-center gap-2.5" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '0 18px', minHeight: '44px' }}>
            <Search size={15} style={{ color: PRIMARY, flexShrink: 0 }} />
            <input
              type="text"
              placeholder={activeTab === 'services' ? 'Rechercher une offre...' : 'Rechercher (client, email, téléphone, adresse)...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '14px', color: '#051004', outline: 'none' }}
            />
          </label>
        </div>
        <div className="sm:w-56">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full"
            style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 18px', minHeight: '44px', fontSize: '14px', color: '#051004', outline: 'none' }}
          >
            {statusOptions.map(option => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'services' ? (
        <>
          {filteredServices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 16px' }}>
              <ChefHat size={40} style={{ color: 'rgba(5,16,4,0.18)', margin: '0 auto 14px' }} />
              <h3 style={{ margin: '0 0 6px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>Aucune offre trouvée</h3>
              <p style={{ margin: '0 0 16px', fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}>
                {searchTerm || filterStatus !== 'all'
                  ? 'Aucune offre ne correspond à vos critères de recherche.'
                  : 'Commencez par ajouter votre première offre traiteur.'}
              </p>
              <button
                onClick={() => navigate('/management/catering/offers/new')}
                className="inline-flex items-center gap-2"
                style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '42px', padding: '0 20px', borderRadius: '999px' }}
              >
                <Plus size={16} />
                Ajouter une offre
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredServices.map((service) => {
                const badge = service.is_active ? SERVICE_BADGE.active : SERVICE_BADGE.inactive;
                return (
                  <div key={service.id} style={{ background: '#FCFBF5', borderRadius: '26px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ position: 'relative', height: '176px', background: '#F4F1E2', flexShrink: 0 }}>
                      {service.image_url && (
                        <img src={service.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                      <span style={{ position: 'absolute', top: '14px', left: '14px', background: badge.bg, color: badge.fg, fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '5px 13px', borderRadius: '999px' }}>
                        {badge.label}
                      </span>
                      <span className="flex items-center gap-1" style={{ position: 'absolute', bottom: '14px', right: '14px', background: 'rgba(252,251,245,0.92)', padding: '5px 11px', borderRadius: '999px' }}>
                        <Star size={13} style={{ color: '#FFC717' }} fill="currentColor" />
                        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 700 }}>{service.rating ? parseFloat(service.rating).toFixed(1) : 'N/A'}</span>
                      </span>
                    </div>

                    <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                      <div className="flex items-center justify-between gap-2">
                        <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '17px' }}>{service.service_name}</strong>
                        <span className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                          {getEventTypeIcon(service.service_type)}
                          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.6)' }}>{getEventTypeName(service.service_type)}</span>
                        </span>
                      </div>
                      <p className="line-clamp-2" style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.68)' }}>{service.service_description}</p>

                      <div className="flex items-center justify-between" style={{ fontSize: '13.5px' }}>
                        <strong>{formatOfferPrice(service)}</strong>
                        <span style={{ fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.6)' }}>{service.min_guests}–{service.max_guests || '∞'} pers.</span>
                      </div>

                      {service.formula_id && formulaNames[service.formula_id] && (
                        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: PRIMARY }}>Formule {formulaNames[service.formula_id]}</span>
                      )}

                      {(service.delivery_available || service.setup_available || service.staff_available || service.equipment_rental) && (
                        <div className="flex flex-wrap gap-1.5">
                          {OPTION_TAGS.filter((o) => service[o.key]).map((o) => (
                            <span key={o.key} style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', background: '#FBF9D9', padding: '4px 11px', borderRadius: '999px' }}>{o.label}</span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between" style={{ marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
                        <span className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setSelectedService(service)}
                            className="w-9 h-9 rounded-full flex items-center justify-center"
                            style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            title="Voir les détails"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/management/catering/offers/${service.id}`)}
                            className="w-9 h-9 rounded-full flex items-center justify-center"
                            style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            title="Modifier"
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteService(service.id)}
                            className="w-9 h-9 rounded-full flex items-center justify-center"
                            style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF0EA'; e.currentTarget.style.color = '#A32C0F'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(5,16,4,0.6)'; }}
                            title="Supprimer"
                          >
                            <Trash2 size={15} />
                          </button>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          {filteredBookings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 16px' }}>
              <Calendar size={40} style={{ color: 'rgba(5,16,4,0.18)', margin: '0 auto 14px' }} />
              <h3 style={{ margin: '0 0 6px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>Aucune réservation</h3>
              <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}>
                {searchTerm || filterStatus !== 'all'
                  ? 'Aucune réservation ne correspond à vos critères.'
                  : 'Aucune réservation pour le moment.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredBookings.map((booking) => {
                const badge = bookingBadge(booking.status);
                return (
                  <div key={booking.id} style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="flex items-center gap-3">
                        <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '19px' }}>{booking.service_name || `Réservation #${booking.id}`}</strong>
                        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.6)' }}>
                          {new Date(booking.event_date).toLocaleDateString('fr-FR')} à {booking.event_time}
                        </span>
                      </span>
                      <span className="flex items-center gap-3">
                        <span style={{ background: badge.bg, color: badge.fg, border: badge.border || 'none', fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, padding: '6px 13px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                          {badge.label}
                        </span>
                        <strong style={{ fontSize: '17px' }}>{formatCurrency(booking.total_amount || 0)}</strong>
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ fontSize: '13.5px' }}>
                      <div>
                        <p style={{ margin: '0 0 4px', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Client</p>
                        {booking.customer_name && <p style={{ margin: 0, fontWeight: 600 }}>{booking.customer_name}</p>}
                        <p style={{ margin: 0, color: 'rgba(5,16,4,0.75)' }}>{booking.contact_email}</p>
                        {booking.contact_phone && <p style={{ margin: 0, color: 'rgba(5,16,4,0.75)' }}>{booking.contact_phone}</p>}
                      </div>
                      <div>
                        <p style={{ margin: '0 0 4px', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Détails</p>
                        <p className="flex items-center gap-1.5" style={{ margin: 0, color: 'rgba(5,16,4,0.75)' }}><Users size={13} />{booking.guest_count} personnes{booking.occasion ? ` · ${getCateringOccasionLabel(booking.occasion)}` : ''}</p>
                        {booking.formula_name && <p style={{ margin: 0, color: 'rgba(5,16,4,0.75)' }}>Formule {booking.formula_name}</p>}
                        {booking.delivery_address && (
                          <p className="flex items-center gap-1.5" style={{ margin: 0, color: 'rgba(5,16,4,0.75)' }}><MapPin size={13} />{booking.delivery_address}</p>
                        )}
                      </div>
                      <div>
                        <p style={{ margin: '0 0 4px', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Paiement</p>
                        <p style={{ margin: 0, color: 'rgba(5,16,4,0.75)' }}>Acompte : {formatCurrency(booking.deposit_amount || 0)} — {booking.deposit_paid ? 'reçu' : 'non versé'}</p>
                      </div>
                    </div>

                    {(chosenOptionLabels(booking.selected_options).length > 0 || booking.selected_dish_names?.length > 0 || booking.dietary_requirements?.length > 0) && (
                      <div className="flex flex-wrap gap-1.5">
                        {chosenOptionLabels(booking.selected_options).map((label) => (
                          <span key={label} style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#DDEECB', color: PRIMARY, padding: '4px 11px', borderRadius: '999px' }}>{label}</span>
                        ))}
                        {getActiveDietaryLabels(booking.dietary_requirements).map((label) => (
                          <span key={label} style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#FEF0EA', color: '#A32C0F', padding: '4px 11px', borderRadius: '999px' }}>{label}</span>
                        ))}
                        {(booking.selected_dish_names || []).map((dish) => (
                          <span key={dish} style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#FBF9D9', padding: '4px 11px', borderRadius: '999px' }}>{dish}</span>
                        ))}
                      </div>
                    )}

                    {booking.special_requests && (
                      <p style={{ margin: 0, background: '#FBF9D9', borderRadius: '18px', padding: '12px 16px', fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.75)' }}>
                        <strong style={{ color: '#051004' }}>Message du client : </strong>{booking.special_requests}
                      </p>
                    )}

                    <div className="flex items-center justify-between gap-2 flex-wrap" style={{ paddingTop: '4px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
                      <button
                        type="button"
                        onClick={() => setSelectedBooking(booking)}
                        className="inline-flex items-center gap-1.5"
                        style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 600, minHeight: '38px', padding: '0 16px', borderRadius: '999px', marginTop: '10px' }}
                      >
                        <Eye size={14} />
                        Voir détails
                      </button>
                      <span className="flex items-center gap-2" style={{ marginTop: '10px' }}>
                        {booking.status === 'pending' && (
                          <button
                            type="button"
                            onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                            style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13px', minHeight: '38px', padding: '0 18px', borderRadius: '999px' }}
                          >
                            Confirmer
                          </button>
                        )}
                        {booking.status === 'confirmed' && eventHasStarted(booking) && (
                          <button
                            type="button"
                            onClick={() => handleUpdateBookingStatus(booking.id, 'completed')}
                            style={{ border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13px', minHeight: '38px', padding: '0 18px', borderRadius: '999px' }}
                          >
                            Marquer comme terminée
                          </button>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ============ POPUP DETAILS SERVICE ============ */}
      {selectedService && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ background: 'rgba(5,16,4,0.5)', zIndex: 50 }}>
          <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '520px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="flex items-center justify-between" style={{ padding: '20px 22px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
              <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '19px' }}>{selectedService.service_name}</strong>
              <button onClick={() => setSelectedService(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'rgba(5,16,4,0.5)' }}>
                <X size={20} />
              </button>
            </div>

            {selectedService.image_url && (
              <div style={{ width: '100%', height: '190px', overflow: 'hidden' }}>
                <img src={selectedService.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}

            <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div className="flex items-center gap-2 flex-wrap">
                {(() => {
                  const badge = selectedService.is_active ? SERVICE_BADGE.active : SERVICE_BADGE.inactive;
                  return <span style={{ background: badge.bg, color: badge.fg, fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '5px 13px', borderRadius: '999px' }}>{badge.label}</span>;
                })()}
                {selectedService.featured && (
                  <span style={{ background: '#FFC717', color: '#051004', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '5px 13px', borderRadius: '999px' }}>Mis en avant</span>
                )}
                {selectedService.service_type && (
                  <span style={{ background: '#FBF9D9', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '5px 13px', borderRadius: '999px' }}>{getEventTypeName(selectedService.service_type)}</span>
                )}
              </div>

              {selectedService.service_description && (
                <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.55, color: 'rgba(5,16,4,0.75)' }}>{selectedService.service_description}</p>
              )}

              <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Tarification</span>
                <div className="grid grid-cols-2 gap-3" style={{ fontSize: '13.5px' }}>
                  {selectedService.base_price != null && (
                    <div><span style={{ color: 'rgba(5,16,4,0.55)' }}>Prix de base</span><p style={{ margin: 0, fontWeight: 600 }}>{formatCurrency(parseFloat(selectedService.base_price))}</p></div>
                  )}
                  {selectedService.price_per_person != null && (
                    <div><span style={{ color: 'rgba(5,16,4,0.55)' }}>Par personne</span><p style={{ margin: 0, fontWeight: 600 }}>{parseFloat(selectedService.price_per_person).toFixed(2)} €</p></div>
                  )}
                  {selectedService.delivery_fee != null && parseFloat(selectedService.delivery_fee) > 0 && (
                    <div><span style={{ color: 'rgba(5,16,4,0.55)' }}>Frais livraison</span><p style={{ margin: 0, fontWeight: 600 }}>{formatCurrency(parseFloat(selectedService.delivery_fee))}</p></div>
                  )}
                  {selectedService.setup_fee != null && parseFloat(selectedService.setup_fee) > 0 && (
                    <div><span style={{ color: 'rgba(5,16,4,0.55)' }}>Frais installation</span><p style={{ margin: 0, fontWeight: 600 }}>{formatCurrency(parseFloat(selectedService.setup_fee))}</p></div>
                  )}
                  {selectedService.staff_fee_per_hour != null && parseFloat(selectedService.staff_fee_per_hour) > 0 && (
                    <div><span style={{ color: 'rgba(5,16,4,0.55)' }}>Personnel / heure</span><p style={{ margin: 0, fontWeight: 600 }}>{parseFloat(selectedService.staff_fee_per_hour).toFixed(2)} €/h</p></div>
                  )}
                  {selectedService.equipment_fee != null && parseFloat(selectedService.equipment_fee) > 0 && (
                    <div><span style={{ color: 'rgba(5,16,4,0.55)' }}>Frais matériel</span><p style={{ margin: 0, fontWeight: 600 }}>{formatCurrency(parseFloat(selectedService.equipment_fee))}</p></div>
                  )}
                  <div><span style={{ color: 'rgba(5,16,4,0.55)' }}>Acompte</span><p style={{ margin: 0, fontWeight: 600 }}>{selectedService.deposit_percentage ?? 30} % du total</p></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3" style={{ fontSize: '13.5px' }}>
                <span className="flex items-start gap-2">
                  <Users size={15} style={{ color: 'rgba(5,16,4,0.4)', marginTop: '2px' }} />
                  <span><span style={{ display: 'block', color: 'rgba(5,16,4,0.55)' }}>Capacité</span><strong>{selectedService.min_guests ?? 1} – {selectedService.max_guests ?? '∞'} pers.</strong></span>
                </span>
                <span className="flex items-start gap-2">
                  <Clock size={15} style={{ color: 'rgba(5,16,4,0.4)', marginTop: '2px' }} />
                  <span><span style={{ display: 'block', color: 'rgba(5,16,4,0.55)' }}>Délai de prévenance</span><strong>{selectedService.min_notice_hours ?? 24} h</strong></span>
                </span>
              </div>

              <div>
                <span style={{ display: 'block', marginBottom: '8px', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Options disponibles</span>
                <div className="flex flex-wrap gap-1.5">
                  {OPTION_TAGS.filter((o) => selectedService[o.key]).map((o) => (
                    <span key={o.key} style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#FBF9D9', padding: '5px 12px', borderRadius: '999px' }}>{o.label}</span>
                  ))}
                  {OPTION_TAGS.every((o) => !selectedService[o.key]) && (
                    <span style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.45)' }}>Aucune option configurée</span>
                  )}
                </div>
              </div>

              {getActiveDietaryLabels(selectedService.dietary_options).length > 0 && (
                <div>
                  <span style={{ display: 'block', marginBottom: '8px', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Régimes pris en charge</span>
                  <p style={{ margin: 0, fontSize: '13.5px' }}>{getActiveDietaryLabels(selectedService.dietary_options).join(' · ')}</p>
                </div>
              )}

              <div className="flex items-center gap-3" style={{ fontSize: '13.5px' }}>
                <span className="flex items-center gap-1"><Star size={14} style={{ color: '#FFC717' }} fill="currentColor" /><strong>{selectedService.rating ? parseFloat(selectedService.rating).toFixed(1) : 'N/A'}</strong><span style={{ color: 'rgba(5,16,4,0.5)' }}>/ 5</span></span>
                <span style={{ color: 'rgba(5,16,4,0.3)' }}>|</span>
                <span style={{ color: 'rgba(5,16,4,0.6)' }}>{selectedService.review_count || 0} avis</span>
              </div>

              <div className="flex gap-3" style={{ paddingTop: '10px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
                <button
                  onClick={() => { setSelectedService(null); navigate(`/management/catering/offers/${selectedService.id}`); }}
                  className="flex-1 inline-flex items-center justify-center gap-2"
                  style={{ border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', borderRadius: '999px' }}
                >
                  <Edit2 size={15} />
                  Modifier
                </button>
                <button
                  onClick={() => { setSelectedService(null); handleDeleteService(selectedService.id); }}
                  style={{ border: '1px solid rgba(163,44,15,0.3)', cursor: 'pointer', background: 'transparent', color: '#A32C0F', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 18px', borderRadius: '999px' }}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ POPUP DETAILS RÉSERVATION ============ */}
      {selectedBooking && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ background: 'rgba(5,16,4,0.5)', zIndex: 50 }}>
          <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '520px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="flex items-center justify-between" style={{ padding: '20px 22px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
              <div>
                <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '19px' }}>{selectedBooking.service_name || `Réservation #${selectedBooking.id}`}</strong>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'rgba(5,16,4,0.55)' }}>
                  {new Date(selectedBooking.event_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  {selectedBooking.event_time && ` à ${selectedBooking.event_time.slice(0, 5)}`}
                </p>
              </div>
              <button onClick={() => setSelectedBooking(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'rgba(5,16,4,0.5)' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div className="flex items-center justify-between">
                {(() => {
                  const badge = bookingBadge(selectedBooking.status);
                  return <span style={{ background: badge.bg, color: badge.fg, border: badge.border || 'none', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, padding: '6px 14px', borderRadius: '999px' }}>{badge.label}</span>;
                })()}
                <strong style={{ fontSize: '20px' }}>{formatCurrency(parseFloat(selectedBooking.total_amount || 0))}</strong>
              </div>

              {(selectedBooking.occasion || selectedBooking.formula_name) && (
                <span className="flex items-center gap-2" style={{ fontSize: '13.5px' }}>
                  <Calendar size={15} style={{ color: 'rgba(5,16,4,0.4)' }} />
                  <span>{[getCateringOccasionLabel(selectedBooking.occasion), selectedBooking.formula_name && `Formule ${selectedBooking.formula_name}`].filter(Boolean).join(' · ')}</span>
                </span>
              )}

              <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Informations client</span>
                {selectedBooking.customer_name && <strong style={{ fontSize: '14px' }}>{selectedBooking.customer_name}</strong>}
                {selectedBooking.contact_email && (
                  <a href={`mailto:${selectedBooking.contact_email}`} className="flex items-center gap-2" style={{ fontSize: '13.5px', color: PRIMARY }}>
                    <Mail size={14} style={{ color: 'rgba(5,16,4,0.4)' }} />{selectedBooking.contact_email}
                  </a>
                )}
                {selectedBooking.contact_phone && (
                  <a href={`tel:${selectedBooking.contact_phone}`} className="flex items-center gap-2" style={{ fontSize: '13.5px', color: PRIMARY }}>
                    <Phone size={14} style={{ color: 'rgba(5,16,4,0.4)' }} />{selectedBooking.contact_phone}
                  </a>
                )}
              </div>

              <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Détails de l'événement</span>
                <div className="grid grid-cols-2 gap-3" style={{ fontSize: '13.5px' }}>
                  <span className="flex items-start gap-2"><Users size={15} style={{ color: 'rgba(5,16,4,0.4)', marginTop: '2px' }} /><span><span style={{ display: 'block', color: 'rgba(5,16,4,0.55)' }}>Invités</span><strong>{selectedBooking.guest_count} pers.</strong></span></span>
                  <span className="flex items-start gap-2"><Clock size={15} style={{ color: 'rgba(5,16,4,0.4)', marginTop: '2px' }} /><span><span style={{ display: 'block', color: 'rgba(5,16,4,0.55)' }}>Heure</span><strong>{selectedBooking.event_time ? selectedBooking.event_time.slice(0, 5) : 'N/A'}</strong></span></span>
                  {selectedBooking.delivery_address && (
                    <span className="col-span-2 flex items-start gap-2"><MapPin size={15} style={{ color: 'rgba(5,16,4,0.4)', marginTop: '2px' }} /><span><span style={{ display: 'block', color: 'rgba(5,16,4,0.55)' }}>Lieu</span><strong>{selectedBooking.delivery_address}</strong></span></span>
                  )}
                  {chosenOptionLabels(selectedBooking.selected_options).length > 0 && (
                    <span className="col-span-2"><span style={{ display: 'block', color: 'rgba(5,16,4,0.55)' }}>Options choisies</span><strong>{chosenOptionLabels(selectedBooking.selected_options).join(' · ')}</strong></span>
                  )}
                  {getActiveDietaryLabels(selectedBooking.dietary_requirements).length > 0 && (
                    <span className="col-span-2"><span style={{ display: 'block', color: 'rgba(5,16,4,0.55)' }}>Régimes</span><strong>{getActiveDietaryLabels(selectedBooking.dietary_requirements).join(' · ')}</strong></span>
                  )}
                  {selectedBooking.selected_dish_names?.length > 0 && (
                    <span className="col-span-2"><span style={{ display: 'block', color: 'rgba(5,16,4,0.55)' }}>Plats choisis</span><strong>{selectedBooking.selected_dish_names.join(' · ')}</strong></span>
                  )}
                </div>
              </div>

              <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Paiement</span>
                <div className="grid grid-cols-2 gap-3" style={{ fontSize: '13.5px' }}>
                  <div><span style={{ color: 'rgba(5,16,4,0.55)' }}>Acompte</span><p style={{ margin: 0, fontWeight: 600, color: selectedBooking.deposit_paid ? PRIMARY : '#A32C0F' }}>{formatCurrency(selectedBooking.deposit_amount || 0)} — {selectedBooking.deposit_paid ? 'reçu' : 'non versé'}</p></div>
                  <div><span style={{ color: 'rgba(5,16,4,0.55)' }}>Total</span><p style={{ margin: 0, fontWeight: 700, fontSize: '16px' }}>{formatCurrency(selectedBooking.total_amount || 0)}</p></div>
                </div>
              </div>

              {selectedBooking.special_requests && (
                <div style={{ background: '#FEF0EA', borderRadius: '20px', padding: '16px 18px' }}>
                  <p style={{ margin: '0 0 4px', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#A32C0F' }}>Message du client</p>
                  <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.5, color: '#7A200B', whiteSpace: 'pre-line' }}>{selectedBooking.special_requests}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2" style={{ paddingTop: '10px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
                {selectedBooking.status === 'pending' && (
                  <button
                    onClick={async () => { if (await handleUpdateBookingStatus(selectedBooking.id, 'confirmed')) setSelectedBooking({ ...selectedBooking, status: 'confirmed' }); }}
                    className="flex-1"
                    style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', borderRadius: '999px' }}
                  >
                    Confirmer la réservation
                  </button>
                )}
                {selectedBooking.status === 'confirmed' && eventHasStarted(selectedBooking) && (
                  <button
                    onClick={async () => { if (await handleUpdateBookingStatus(selectedBooking.id, 'completed')) setSelectedBooking({ ...selectedBooking, status: 'completed' }); }}
                    className="flex-1"
                    style={{ border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', borderRadius: '999px' }}
                  >
                    Marquer comme terminée
                  </button>
                )}
                {(selectedBooking.status === 'pending' || selectedBooking.status === 'confirmed') && (
                  <button
                    onClick={async () => { if (await handleUpdateBookingStatus(selectedBooking.id, 'cancelled')) setSelectedBooking({ ...selectedBooking, status: 'cancelled' }); }}
                    style={{ border: '1px solid rgba(163,44,15,0.3)', cursor: 'pointer', background: 'transparent', color: '#A32C0F', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 18px', borderRadius: '999px' }}
                  >
                    Annuler
                  </button>
                )}
                <button
                  onClick={() => setSelectedBooking(null)}
                  style={{ border: 'none', cursor: 'pointer', background: '#FBF9D9', color: '#051004', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 18px', borderRadius: '999px' }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantCatering;
